// Settings › Branding — the tenant's color scheme, logo, favicon and SEO & Social.
//
// The page is called Branding in the UI. This component, `ThemeConfig`,
// `tenant_theme_settings` and `current_theme` all keep their names on purpose: the theme
// loader, the proposals branding path and the customer portal all speak that vocabulary, so
// the rename is a label change, not a data change.
//
// Replaces a six-tab layout (Themes / Custom / Logo / Favicon / Sidebar / SEO) with a flat
// page: one scheme gallery split by origin, then a brand sheet for the assets. The metadata
// editor opens as a dialog rather than owning a tab, because the sheet already shows the
// search snippet it produces and 270 lines of form buried that.

import React, { useCallback, useMemo, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { useTheme } from '@/contexts/ThemeContext';
import { useTenant } from '@/contexts/TenantContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useToast } from '@/components/ui/use-toast';
import { supabase } from '@/lib/supabaseClient';
import type { SEOConfig, ThemeConfig } from '@/types/theme';
import { buildSchemeGallery, type SchemeCard } from './branding/brandingHelpers';
import { SchemeGallery } from './branding/SchemeGallery';
import { CompanyBrandPanel } from './branding/CompanyBrandPanel';
import { SeoSocialDialog } from './branding/SeoSocialDialog';
import { EnhancedThemeEditor } from './EnhancedThemeEditor';

export const ThemeTab: React.FC = () => {
  const {
    currentTheme,
    themeSettings,
    setTheme,
    updateCustomLogo,
    updateCustomLogoDark,
    updateLogoDarkTreatment,
    updateCustomFavicon,
    updateCustomSEO,
    createCustomTheme,
    updateCustomTheme,
    deleteCustomTheme,
    updateSidebarColor,
    isLoading,
    canManageTheme,
  } = useTheme();
  const { currentTenant, getCurrentTenantId } = useTenant();
  const { toast } = useToast();

  const [editingTheme, setEditingTheme] = useState<ThemeConfig | null>(null);
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [seoOpen, setSeoOpen] = useState(false);

  const gallery = useMemo(
    () => buildSchemeGallery(themeSettings.customThemes, themeSettings.currentTheme),
    [themeSettings.customThemes, themeSettings.currentTheme],
  );

  const logoUrl = themeSettings.customLogo ?? currentTheme.logo ?? null;
  // The uploaded dark variant. Only used when logoDarkTreatment is 'custom'; null means
  // nothing has been uploaded, which that treatment degrades to 'same' for.
  const logoDarkUrl = themeSettings.customLogoDark?.trim() ? themeSettings.customLogoDark : null;
  const faviconUrl = themeSettings.customFavicon ?? currentTheme.favicon ?? null;

  const seoValue: SEOConfig = useMemo(
    () => ({
      pageTitle: themeSettings.customSEO?.pageTitle || currentTheme.seo?.pageTitle || 'Joules Project Management',
      metaDescription: themeSettings.customSEO?.metaDescription || currentTheme.seo?.metaDescription || '',
      metaKeywords: themeSettings.customSEO?.metaKeywords || currentTheme.seo?.metaKeywords || '',
      ogTitle: themeSettings.customSEO?.ogTitle || currentTheme.seo?.ogTitle || '',
      ogDescription: themeSettings.customSEO?.ogDescription || currentTheme.seo?.ogDescription || '',
      ogImage: themeSettings.customSEO?.ogImage || currentTheme.seo?.ogImage || '',
      twitterCard: themeSettings.customSEO?.twitterCard || currentTheme.seo?.twitterCard || 'summary_large_image',
      twitterTitle: themeSettings.customSEO?.twitterTitle || currentTheme.seo?.twitterTitle || '',
      twitterDescription:
        themeSettings.customSEO?.twitterDescription || currentTheme.seo?.twitterDescription || '',
      twitterImage: themeSettings.customSEO?.twitterImage || currentTheme.seo?.twitterImage || '',
      canonicalUrl: themeSettings.customSEO?.canonicalUrl || currentTheme.seo?.canonicalUrl || '',
      robotsMeta: themeSettings.customSEO?.robotsMeta || currentTheme.seo?.robotsMeta || 'index, follow',
    }),
    [themeSettings.customSEO, currentTheme.seo],
  );

  /** Shared uploader. Tenant-prefixed path, `logos` bucket. */
  const uploadBrandAsset = useCallback(
    async (file: File): Promise<string> => {
      const uploadTenantId = await getCurrentTenantId();
      const fileExt = file.name.split('.').pop();
      const filePath = `${uploadTenantId}/${crypto.randomUUID()}.${fileExt}`;

      const { error: uploadError } = await supabase.storage.from('logos').upload(filePath, file);
      if (uploadError) {
        toast({
          title: 'Error',
          description: 'Failed to upload the image. Please check your file and try again.',
          variant: 'destructive',
        });
        throw uploadError;
      }

      const {
        data: { publicUrl },
      } = supabase.storage.from('logos').getPublicUrl(filePath);
      return publicUrl;
    },
    [getCurrentTenantId, toast],
  );

  const requireAdmin = (): boolean => {
    if (canManageTheme) return true;
    toast({
      title: 'Access Denied',
      description: 'Only administrators can change branding.',
      variant: 'destructive',
    });
    return false;
  };

  const handleSchemeSelect = async (card: SchemeCard) => {
    if (!requireAdmin()) return;
    try {
      await setTheme(card.id);
      toast({ title: 'Scheme updated', description: `Now using ${card.name}.` });
    } catch {
      toast({ title: 'Error', description: 'Failed to update the scheme.', variant: 'destructive' });
    }
  };

  const handleCreateTheme = async (theme: Omit<ThemeConfig, 'id' | 'createdAt'>) => {
    try {
      await createCustomTheme(theme);
      setIsEditorOpen(false);
      setEditingTheme(null);
      toast({ title: 'Scheme created', description: 'Your scheme is ready to use.' });
    } catch {
      toast({ title: 'Error', description: 'Failed to create the scheme.', variant: 'destructive' });
    }
  };

  const handleUpdateTheme = async (theme: Omit<ThemeConfig, 'id' | 'createdAt'>) => {
    if (!editingTheme) return;
    try {
      await updateCustomTheme(editingTheme.id, theme);
      setEditingTheme(null);
      setIsEditorOpen(false);
      toast({ title: 'Scheme updated', description: 'Your changes have been saved.' });
    } catch {
      toast({ title: 'Error', description: 'Failed to update the scheme.', variant: 'destructive' });
    }
  };

  const handleDeleteTheme = async (card: SchemeCard) => {
    if (!requireAdmin()) return;
    // Deleting the live scheme drops the tenant back to the default palette, which is a
    // whole-app visual change — worth a confirm even though it is recoverable.
    if (card.isActive) {
      const confirmed = window.confirm(
        `"${card.name}" is the scheme currently in use. Deleting it reverts the app to the default palette. Delete anyway?`,
      );
      if (!confirmed) return;
    }
    try {
      await deleteCustomTheme(card.id);
      toast({ title: 'Scheme deleted', description: `"${card.name}" has been removed.` });
    } catch {
      toast({ title: 'Error', description: 'Failed to delete the scheme.', variant: 'destructive' });
    }
  };

  const handleSaveSEO = async (seo: SEOConfig) => {
    if (!requireAdmin()) return;
    try {
      await updateCustomSEO(seo);
      toast({ title: 'SEO & Social saved', description: 'Your changes have been saved.' });
    } catch {
      toast({ title: 'Error', description: 'Failed to save SEO & Social settings.', variant: 'destructive' });
    }
  };

  const handleSidebarColorChange = async (color: string) => {
    if (!requireAdmin()) return;
    try {
      await updateSidebarColor(color);
    } catch {
      toast({ title: 'Error', description: 'Failed to update the sidebar color.', variant: 'destructive' });
    }
  };

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold tracking-tight text-foreground">Branding</h2>
        <p className="text-muted-foreground">
          Colors, logo and search previews for {currentTenant?.company_name || 'your company'}.
        </p>
        {!canManageTheme && (
          <div className="mt-4 rounded-lg border border-primary/20 bg-primary/5 p-4">
            <p className="text-sm text-primary">
              <strong>Note:</strong> Only administrators can modify branding. You can view the current
              configuration.
            </p>
          </div>
        )}
      </div>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Color scheme</CardTitle>
        </CardHeader>
        <CardContent>
          <SchemeGallery
            builtIn={gallery.builtIn}
            yours={gallery.yours}
            selectedId={themeSettings.currentTheme}
            onSelect={(card) => void handleSchemeSelect(card)}
            canEdit={canManageTheme}
            onCreate={() => {
              setEditingTheme(null);
              setIsEditorOpen(true);
            }}
            onEdit={(card) => {
              setEditingTheme(card.theme);
              setIsEditorOpen(true);
            }}
            onDelete={(card) => void handleDeleteTheme(card)}
          />
        </CardContent>
      </Card>

      <CompanyBrandPanel
        currentTheme={currentTheme}
        logoUrl={logoUrl}
        logoDarkUrl={logoDarkUrl}
        logoDarkTreatment={themeSettings.logoDarkTreatment}
        faviconUrl={faviconUrl}
        seo={seoValue}
        canEdit={canManageTheme}
        sidebarColorOverride={themeSettings.sidebarColorOverride}
        onUploadAsset={uploadBrandAsset}
        onLogoChange={updateCustomLogo}
        onLogoDarkChange={updateCustomLogoDark}
        onTreatmentChange={updateLogoDarkTreatment}
        onFaviconChange={updateCustomFavicon}
        onSidebarColorChange={handleSidebarColorChange}
        onEditSeo={() => setSeoOpen(true)}
      />

      <Dialog
        open={isEditorOpen}
        onOpenChange={(open) => {
          setIsEditorOpen(open);
          if (!open) setEditingTheme(null);
        }}
      >
        <DialogContent className="max-h-[90vh] max-w-4xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingTheme ? 'Edit scheme' : 'Create scheme'}</DialogTitle>
            <DialogDescription>
              Design a color scheme for {currentTenant?.company_name || 'your company'}.
            </DialogDescription>
          </DialogHeader>
          <EnhancedThemeEditor
            theme={editingTheme || undefined}
            onSave={editingTheme ? handleUpdateTheme : handleCreateTheme}
            onCancel={() => {
              setIsEditorOpen(false);
              setEditingTheme(null);
            }}
            isEditing={!!editingTheme}
          />
        </DialogContent>
      </Dialog>

      <SeoSocialDialog
        open={seoOpen}
        onOpenChange={setSeoOpen}
        value={seoValue}
        canEdit={canManageTheme}
        onSave={handleSaveSEO}
      />
    </div>
  );
};
